import React, { useState } from "react";
import { Box, Button, TextField, Typography, Tooltip } from "@mui/material";
import { Formik, Form } from "formik";
import { CreateOutlined } from "@mui/icons-material";
import NewPhoneNumberComponent from "../../../common/components/new-phone-input-component/new-phone-input-component";
import { basicInfoFormValidationSchema } from "./form-validations";
import { normalizePhoneNumber } from "../../../utils/utils";
import { updatePreneedProfileBasicDetails } from "../../../api/preneed-section/preneed-profile-mgmt.api";
import { AlertComponent } from "../../../common/components";
import "./basic-information-form.scss";
interface OperationalHours {
  days: Record<string, boolean>;
  openingTime: string;
}

interface BasicInformationFormValues {
  businessName: string;
  planner_id: string;
  businessDescription: string;
  contactEmail: string;
  contactPhone: string;
  contactName: string;
  contactPosition?: string;
  operationalHours: OperationalHours;
}

interface BasicInformationFormProps {
  data: any;
  editMode: boolean;
  onEditModeChange: (mode: boolean) => void;
  innerRef?: React.Ref<any>;
  refresh?: () => void;
}
const BasicInformationForm: React.FC<BasicInformationFormProps> = ({
  data,
  editMode,
  onEditModeChange,
  innerRef,
  refresh,
}) => {
  const [countryCode, setCountryCode] = useState<string>("+1");
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const formInitialValues: BasicInformationFormValues = {
    businessName: data?.businessName || "",
    planner_id: data?.planner_id || data?.userId || "",
    businessDescription: data?.businessDescription || "",
    contactEmail: data?.contactEmail || "",
    contactPhone: normalizePhoneNumber(data?.contactPhone) || "",
    contactName: data?.contactName || "",
    contactPosition: data?.contactPosition || "",
    operationalHours: {
      days: data?.operationalHours?.days || {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      openingTime: data?.operationalHours?.openingTime || "",
    },
  };

  const allowAlphabetsOnly = (field: string, setFieldValue: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      setFieldValue(field, value);
    }
  };

  const allowDesignationChars = (field: string, setFieldValue: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s-]*$/.test(value)) {
      setFieldValue(field, value);
    }
  };

  // Submit handler
  const handleSubmit = async (values: BasicInformationFormValues) => {
    try {
      const phoneWithCountryCode = values.contactPhone.startsWith("+")
        ? values.contactPhone
        : `${countryCode}${values.contactPhone}`;

      // Construct payload according to CURL
      const payload = {
        businessName: values.businessName,
        businessDescription: values.businessDescription,
        contactEmail: values.contactEmail,
        contactPhone: phoneWithCountryCode,
        contactName: values.contactName,
        contactPosition: values.contactPosition,
        operationalHours: values.operationalHours,
      };

      const response = await updatePreneedProfileBasicDetails(payload);

      if (response?.data?.success) {
        console.log("DETAILS UPDATED!");
        setAlertOpen(true);
        setAlertType("success");
        setAlertMessage("Saved successfully");
        onEditModeChange(false);
        if (refresh) refresh();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Box className="basic-information-form">
      {/* Form */}
      <Formik
        initialValues={formInitialValues}
        validationSchema={basicInfoFormValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        innerRef={innerRef}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          resetForm,
          submitForm,
        }) => (
          <>
            {/* Header */}
            <Box className="basic-information-form__header">
              {!editMode ? (
                <CreateOutlined
                  onClick={() => onEditModeChange(true)}
                  className="basic-information__edit-icon"
                  fontSize="small"
                />
              ) : (
                <Box className="basic-information-form__actions">
                  <Button
                    variant="outlined"
                    className="basic-information-form__cancel-btn"
                    onClick={() => {
                      resetForm();
                      onEditModeChange(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      submitForm();
                    }}
                    className="basic-information-form__save-btn"
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>

            <Form id="basic-info-form" className="basic-information-form__form">
              <Box className="basic-information-form__row">
                {/* Business / Partner Name */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Business/Partner Name{" "}
                    <span className="basic-information-form__required">*</span>
                  </Typography>
                  {editMode ? (
                    <TextField
                      name="businessName"
                      value={values.businessName}
                      onChange={allowAlphabetsOnly("businessName", setFieldValue)}
                      onBlur={handleBlur}
                      error={
                        touched.businessName && Boolean(errors.businessName)
                      }
                      helperText={touched.businessName && errors.businessName}
                      fullWidth
                      size="small"
                      placeholder="Enter Full Length Business / Partner Name"
                      inputProps={{ maxLength: 30 }}
                    />
                  ) : (
                    <Tooltip
                      title={values.businessName || "-"}
                      arrow
                      placement="top-start"
                    >
                      <Typography className="basic-information-form__value">
                        {values.businessName || "-"}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                {/* Partner ID */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Partner ID{" "}
                    <span className="basic-information-form__required">*</span>
                  </Typography>
                  {editMode ? (
                    <TextField
                      name="planner_id"
                      value={values.planner_id}
                      onBlur={handleBlur}
                      InputProps={{ readOnly: true }}
                      fullWidth
                      disabled
                      size="small"
                      className="basic-information-form__readonly"
                    />
                  ) : (
                    <Typography className="basic-information-form__value">
                      {values.planner_id || "-"}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Detailed Description - full width */}
              <Box className="basic-information-form__field basic-information-form__full">
                <Typography className="basic-information-form__label">
                  Detail Description{" "}
                  <span className="basic-information-form__required">*</span>
                </Typography>
                {editMode ? (
                  <TextField
                    name="businessDescription"
                    value={values.businessDescription}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.businessDescription &&
                      Boolean(errors.businessDescription)
                    }
                    helperText={
                      touched.businessDescription && errors.businessDescription
                    }
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder="Enter About us/Detail Description"
                    inputProps={{ maxLength: 500 }}
                  />
                ) : (
                  <Typography
                    className="basic-information-form__value"
                    sx={{
                      whiteSpace: "pre-wrap !important",
                      wordBreak: "break-word !important",
                    }}
                  >
                    {values.businessDescription || "-"}
                  </Typography>
                )}
              </Box>

              <Box className="basic-information-form__row">
                {/* Email */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Email Address{" "}
                    <span className="basic-information-form__required">*</span>
                  </Typography>
                  {editMode ? (
                    <TextField
                      name="contactEmail"
                      type="email"
                      value={values.contactEmail}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length > 50) return;

                        // Don't allow special chars after @ and .
                        const parts = val.split("@");
                        if (parts.length > 1) {
                          const domain = parts[1];
                          const domainParts = domain.split(".");
                          // Clean domain name parts (only allow alphanum)
                          const cleanedDomainParts = domainParts.map((p) =>
                            p.replace(/[^a-zA-Z0-9]/g, ""),
                          );
                          const cleanedDomain = cleanedDomainParts.join(".");
                          const finalVal = `${parts[0]}@${cleanedDomain}`;
                          setFieldValue("contactEmail", finalVal);
                        } else {
                          setFieldValue("contactEmail", val);
                        }
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.contactEmail && Boolean(errors.contactEmail)
                      }
                      helperText={touched.contactEmail && errors.contactEmail}
                      fullWidth
                      size="small"
                      placeholder="Enter Email Address"
                    />
                  ) : (
                    <Tooltip
                      title={values.contactEmail || "-"}
                      arrow
                      placement="top-start"
                    >
                      <Typography className="basic-information-form__value">
                        {values.contactEmail || "-"}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                {/* Contact Number */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Contact Number{" "}
                    <span className="basic-information-form__required">*</span>
                  </Typography>
                  <Box
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        height: "50px",
                        width: "100% !important",
                      },
                      "& .MuiInputBase-root": {
                        fontSize: "14px",
                        height: "40px",
                        padding: "12px 6px",
                      },
                      "& input": { color: "#104145" },
                    }}
                  >
                    {editMode ? (
                      <>
                        <NewPhoneNumberComponent
                          value={values.contactPhone}
                          onChange={(value: string) =>
                            setFieldValue("contactPhone", value)
                          }
                          countryCode={countryCode}
                          onCountryChange={setCountryCode}
                          placeholder="Enter Phone Number"
                          error={
                            touched.contactPhone && Boolean(errors.contactPhone)
                          }
                          showHelper={true}
                        />
                      </>
                    ) : (
                      <Tooltip
                        title={values.contactPhone || "-"}
                        arrow
                        placement="top-start"
                      >
                        <Typography className="basic-information-form__value">
                          {values.contactPhone || "-"}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {/* <Box className="basic-information-form__field basic-information-form__half">
                <Typography className="basic-information-form__label">
                  Contact Number{" "}
                  <span className="basic-information-form__required">*</span>
                </Typography>
                {editMode ? (
                  <TextField
                    name="contactPhone"
                    value={values.contactPhone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.contactPhone && Boolean(errors.contactPhone)
                    }
                    helperText={touched.contactPhone && errors.contactPhone}
                    fullWidth
                    size="small"
                    placeholder="Enter Phone Number"
                  />
                ) : (
                  <Typography className="basic-information-form__value">
                    {values.contactPhone || "-"}
                  </Typography>
                )}
              </Box> */}
              </Box>

              <Box className="basic-information-form__row">
                {/* Contact Person Name */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Contact Person Name{" "}
                    <span className="basic-information-form__required">*</span>
                  </Typography>
                  {editMode ? (
                    <TextField
                      name="contactName"
                      value={values.contactName}
                      onChange={allowAlphabetsOnly("contactName", setFieldValue)}
                      onBlur={handleBlur}
                      error={touched.contactName && Boolean(errors.contactName)}
                      helperText={touched.contactName && errors.contactName}
                      fullWidth
                      size="small"
                      placeholder="Enter Contact Person Name"
                      inputProps={{ maxLength: 30 }}
                    />
                  ) : (
                    <Tooltip
                      title={values.contactName || "-"}
                      arrow
                      placement="top-start"
                    >
                      <Typography className="basic-information-form__value">
                        {values.contactName || "-"}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                {/* Contact Person Designation */}
                <Box className="basic-information-form__field basic-information-form__half">
                  <Typography className="basic-information-form__label">
                    Contact Person Designation
                  </Typography>
                  {editMode ? (
                    <TextField
                      name="contactPosition"
                      value={values.contactPosition}
                      onChange={allowDesignationChars("contactPosition", setFieldValue)}
                      onBlur={handleBlur}
                      error={
                        touched.contactPosition &&
                        Boolean(errors.contactPosition)
                      }
                      helperText={
                        touched.contactPosition && errors.contactPosition
                      }
                      fullWidth
                      size="small"
                      placeholder="Enter Contact Person Designation"
                      inputProps={{ maxLength: 30 }}
                    />
                  ) : (
                    <Tooltip
                      title={values.contactPosition || "-"}
                      arrow
                      placement="top-start"
                    >
                      <Typography className="basic-information-form__value">
                        {values.contactPosition || "-"}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Form>
          </>
        )}
      </Formik>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default BasicInformationForm;
